/**
 * KioskShell.tsx
 *
 * Fullscreen customer-facing kiosk shell:
 * - Intercepts navigation keys in standalone/Electron kiosks.
 * - Manages the Attractor Loop -> Catalog -> Self-Checkout lifecycle.
 * - WCAG 2.2 AAA Accessibility Toolbar (Wheelchair reach mode, High-Contrast, Text Zoom).
 * - 5-tap supervisor escape gate on the brand logo.
 */

import React, { useEffect, useState } from 'react';
import type {
  AccessibilitySettings,
  GuestCart,
  GuestCartItem,
  GuestCheckoutCommand,
  GuestOrderClaimToken,
  KioskConfig,
  PublicProductProjection,
} from '../../plugins/storefront/storefront-contracts.js';
import { KioskSessionManager } from '../../plugins/storefront/kiosk-session-manager.js';
import { KioskGovernanceService } from '../../plugins/storefront/kiosk-governance-service.js';
import { KioskAttractorView } from './KioskAttractorView.tsx';
import { KioskCatalogView } from './KioskCatalogView.tsx';
import { KioskSelfCheckoutModal } from './KioskSelfCheckoutModal.tsx';
import { KioskInactivityWarningModal } from './KioskInactivityWarningModal.tsx';

export interface KioskShellProps {
  readonly config: KioskConfig;
  readonly products: readonly PublicProductProjection[];
  readonly onExecuteCheckout: (command: GuestCheckoutCommand) => Promise<{
    orderId: string;
    orderNumber: string;
    claimToken: GuestOrderClaimToken;
    pickupQrToken: string;
  }>;
  readonly onExitKiosk?: () => void;
}

export const KioskShell: React.FC<KioskShellProps> = ({
  config,
  products,
  onExecuteCheckout,
  onExitKiosk,
}) => {
  const [sessionManager] = useState(() => new KioskSessionManager(config));
  const [governanceService] = useState(() => new KioskGovernanceService(config));

  const [currentView, setCurrentView] = useState<'attractor' | 'catalog' | 'checkout' | 'completed'>('attractor');
  const [cart, setCart] = useState<GuestCart>(() => sessionManager.getState().activeCart);
  const [warningRemaining, setWarningRemaining] = useState<number | null>(null);
  const [showSupervisorModal, setShowSupervisorModal] = useState(false);
  const [supervisorPin, setSupervisorPin] = useState('');
  const [supervisorError, setSupervisorError] = useState<string>();

  const [accessibility, setAccessibility] = useState<AccessibilitySettings>({
    wheelchairMode: false,
    highContrast: false,
    textMagnification: 1.0,
    screenReaderEnabled: false,
    language: config.defaultLanguage,
  });

  // Watchdog lifecycle
  useEffect(() => {
    sessionManager.startWatchdog(
      (remaining) => {
        setWarningRemaining(remaining);
      },
      () => {
        setWarningRemaining(null);
        setCurrentView('attractor');
        setCart(sessionManager.getState().activeCart);
      }
    );

    return () => {
      sessionManager.stopWatchdog();
    };
  }, [sessionManager]);

  const handleUserActivity = () => {
    sessionManager.recordInteraction();
    if (warningRemaining !== null) {
      setWarningRemaining(null);
    }
  };

  const handleStartShopping = () => {
    handleUserActivity();
    sessionManager.transitionTo('catalog');
    setCurrentView('catalog');
  };

  const handleAddItem = (item: GuestCartItem) => {
    handleUserActivity();
    setCart((prev) => {
      const existing = prev.items.find((i) => i.variantId === item.variantId);
      const nextItems = existing
        ? prev.items.map((i) =>
            i.variantId === item.variantId
              ? { ...i, quantity: i.quantity + item.quantity, totalMinor: (i.quantity + item.quantity) * i.unitPriceMinor }
              : i
          )
        : [...prev.items, item];

      const subtotalMinor = nextItems.reduce((sum, i) => sum + i.totalMinor, 0);
      const estimatedTaxMinor = Math.round((subtotalMinor * 825) / 10000);
      const grandTotalMinor = subtotalMinor + estimatedTaxMinor;

      return {
        ...prev,
        items: nextItems,
        subtotalMinor,
        estimatedTaxMinor,
        grandTotalMinor,
        updatedAt: Date.now(),
      };
    });
  };

  const handleUpdateQuantity = (variantId: string, delta: number) => {
    handleUserActivity();
    setCart((prev) => {
      const nextItems = prev.items
        .map((i) => {
          if (i.variantId === variantId) {
            const nextQty = i.quantity + delta;
            if (nextQty <= 0) return null;
            return { ...i, quantity: nextQty, totalMinor: nextQty * i.unitPriceMinor };
          }
          return i;
        })
        .filter(Boolean) as GuestCartItem[];

      const subtotalMinor = nextItems.reduce((sum, i) => sum + i.totalMinor, 0);
      const estimatedTaxMinor = Math.round((subtotalMinor * 825) / 10000);
      const grandTotalMinor = subtotalMinor + estimatedTaxMinor;

      return {
        ...prev,
        items: nextItems,
        subtotalMinor,
        estimatedTaxMinor,
        grandTotalMinor,
        updatedAt: Date.now(),
      };
    });
  };

  const handleResetSession = () => {
    sessionManager.zeroTraceReset();
    setCart(sessionManager.getState().activeCart);
    setCurrentView('attractor');
  };

  const handleBrandLogoTap = () => {
    const res = governanceService.recordEscapeTap();
    if (res.isTriggered) {
      setShowSupervisorModal(true);
    }
  };

  const handleVerifySupervisor = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const ok = await governanceService.verifySupervisorPin(supervisorPin);
      if (ok) {
        setShowSupervisorModal(false);
        setSupervisorPin('');
        setSupervisorError(undefined);
        onExitKiosk?.();
      } else {
        setSupervisorError('Incorrect supervisor PIN');
        setSupervisorPin('');
      }
    } catch (err) {
      setSupervisorError((err as Error).message);
    }
  };

  const toggleWheelchair = () => {
    handleUserActivity();
    setAccessibility((a) => ({ ...a, wheelchairMode: !a.wheelchairMode }));
  };

  const toggleHighContrast = () => {
    handleUserActivity();
    setAccessibility((a) => ({ ...a, highContrast: !a.highContrast }));
  };

  const cycleZoom = () => {
    handleUserActivity();
    setAccessibility((a) => ({
      ...a,
      textMagnification: a.textMagnification === 1.0 ? 1.25 : a.textMagnification === 1.25 ? 1.5 : 1.0,
    }));
  };

  // High contrast & accessibility styles
  const isHighContrast = accessibility.highContrast;
  const zoomFactor = accessibility.textMagnification;

  return (
    <div
      onClick={handleUserActivity}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: isHighContrast ? '#000' : '#090d16',
        color: isHighContrast ? '#ffff00' : '#fff',
        fontSize: `${zoomFactor * 100}%`,
        display: 'flex',
        flexDirection: 'column',
        userSelect: 'none',
        overflow: 'hidden',
      }}
    >
      {/* Top Accessibility Bar */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '8px 16px',
          backgroundColor: isHighContrast ? '#111' : '#0f172a',
          borderBottom: isHighContrast ? '2px solid #ffff00' : '1px solid #1e293b',
          zIndex: 100,
        }}
      >
        <div
          onClick={handleBrandLogoTap}
          style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
        >
          <span style={{ fontSize: '1.2rem' }}>🌿</span>
          <span style={{ fontWeight: 800, fontSize: '0.95rem' }}>{config.storeName}</span>
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            onClick={toggleWheelchair}
            style={{
              padding: '6px 12px',
              borderRadius: 6,
              border: accessibility.wheelchairMode ? '2px solid #38bdf8' : '1px solid #334155',
              backgroundColor: accessibility.wheelchairMode ? '#0284c7' : '#1e293b',
              color: '#fff',
              fontSize: '0.8rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              minHeight: 40,
            }}
          >
            ♿ Reach Mode
          </button>
          <button
            type="button"
            onClick={toggleHighContrast}
            style={{
              padding: '6px 12px',
              borderRadius: 6,
              border: isHighContrast ? '2px solid #ffff00' : '1px solid #334155',
              backgroundColor: isHighContrast ? '#ffff00' : '#1e293b',
              color: isHighContrast ? '#000' : '#fff',
              fontSize: '0.8rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              minHeight: 40,
            }}
          >
            👁️ High Contrast
          </button>
          <button
            type="button"
            onClick={cycleZoom}
            style={{
              padding: '6px 12px',
              borderRadius: 6,
              border: '1px solid #334155',
              backgroundColor: '#1e293b',
              color: '#38bdf8',
              fontSize: '0.8rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              minHeight: 40,
            }}
          >
            🔍 Zoom ({zoomFactor}x)
          </button>
        </div>
      </div>

      {/* Main Interactive Stage */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          marginTop: accessibility.wheelchairMode ? '40vh' : 0, // Wheelchair reach mode
          transition: 'margin-top 0.3s ease',
          overflow: 'hidden',
        }}
      >
        {currentView === 'attractor' ? (
          <KioskAttractorView
            storeName={config.storeName}
            onStartShopping={handleStartShopping}
            onBrandLogoTap={handleBrandLogoTap}
          />
        ) : (
          <KioskCatalogView
            products={products}
            cart={cart}
            onAddItem={handleAddItem}
            onUpdateQuantity={handleUpdateQuantity}
            onOpenCheckout={() => setCurrentView('checkout')}
            onResetSession={handleResetSession}
          />
        )}
      </div>

      {/* Self-Checkout Modal */}
      {currentView === 'checkout' && (
        <KioskSelfCheckoutModal
          cart={cart}
          onExecuteCheckout={onExecuteCheckout}
          onClose={() => setCurrentView('catalog')}
          onFinishOrder={() => handleResetSession()}
        />
      )}

      {/* Inactivity Warning Countdown Dialog */}
      {warningRemaining !== null && (
        <KioskInactivityWarningModal
          remainingSeconds={warningRemaining}
          onContinue={handleUserActivity}
          onResetNow={handleResetSession}
        />
      )}

      {/* Supervisor Escape PIN Modal */}
      {showSupervisorModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 999999,
          }}
        >
          <form
            onSubmit={handleVerifySupervisor}
            style={{
              backgroundColor: '#0f172a',
              padding: 28,
              borderRadius: 16,
              width: 360,
              textAlign: 'center',
              border: '2px solid #38bdf8',
            }}
          >
            <h3 style={{ margin: '0 0 12px 0', fontSize: '1.3rem' }}>🔒 Supervisor Unlock</h3>
            <p style={{ fontSize: '0.85rem', color: '#94a3b8', margin: '0 0 16px 0' }}>
              Enter supervisor PIN to exit kiosk fullscreen mode:
            </p>

            <input
              type="password"
              placeholder="Enter PIN..."
              autoFocus
              value={supervisorPin}
              onChange={(e) => setSupervisorPin(e.target.value)}
              style={{
                width: '100%',
                padding: 12,
                fontSize: '1.2rem',
                textAlign: 'center',
                backgroundColor: '#1e293b',
                color: '#fff',
                border: '1px solid #475569',
                borderRadius: 8,
                marginBottom: 12,
              }}
            />

            {supervisorError && (
              <div style={{ color: '#ef4444', fontSize: '0.85rem', marginBottom: 12, fontWeight: 'bold' }}>
                {supervisorError}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                type="button"
                onClick={() => {
                  setShowSupervisorModal(false);
                  setSupervisorPin('');
                  setSupervisorError(undefined);
                }}
                style={{
                  flex: 1,
                  padding: '10px 0',
                  backgroundColor: '#334155',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                style={{
                  flex: 1,
                  padding: '10px 0',
                  backgroundColor: '#38bdf8',
                  color: '#0f172a',
                  border: 'none',
                  borderRadius: 8,
                  fontWeight: 'bold',
                  cursor: 'pointer',
                }}
              >
                Unlock
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};
